<template>
    <div
        ref="wrapperRef"
        class="widget-templates-settings"
        style="scroll-margin-top: 24px"
    >
        <div
            v-if="customFormVisible"
            class="widget-templates-settings__custom"
        >
            <WidgetTemplateCustomSetup
                v-if="widgetToSetup"
                ref="widgetTemplateCustomSetupRef"
                v-model="widgetToSetup"
                :template-name="widgetToSetupTemplate"
                @back="onCloseCustomEditForm"
            />
        </div>
        <div v-else>
            <div v-if="!isSummaryView">
                <div class="text-base font-semibold main-text-color mb-8">
                    {{ $t('widget.add.generalForAllWidgets') }}
                </div>
                <div class="text-base font-semibold mb-3">
                    <WidgetTemplateSettingsForm
                        ref="widgetTemplateSettingsFormRef"
                        v-model="widgetUniqueTemplateModel"
                        :uniq-templates-configs="uniqTemplatesConfigs"
                        :show-statistics-to-display="showStatisticsToDisplay"
                        :show-status-select="showStatusSelect"
                        :show-time-frame="showTimeFrame"
                        @update:configs="updateFilterItem"
                    />
                </div>
            </div>
            <div
                v-if="isAllCustomSettings"
                class="mb-5"
            >
                <WidgetCustomisedMessage>
                    {{ $t('widget.add.allCustomisedSettings') }}
                </WidgetCustomisedMessage>
            </div>
            <div class="bg-light-bg rounded-md p-3 lg:p-6">
                <div>
                    <div class="text-base font-semibold mb-2">
                        {{ $t('widget.add.selectedWidgets') }}
                    </div>
                    <div class="text-sm">
                        {{ $t('widget.add.selectedWidgetsInfo') }}
                    </div>
                </div>
                <div class="pt-3 lg:pt-6 space-y-3">
                    <WidgetTemplateSelected
                        v-for="widget in widgetList"
                        :key="widget.uid"
                        :widget="widget"
                        :copy-disabled="totalWidgets === MAX_WIDGETS_LIMIT"
                        @widget:delete="onDeleteSelectedWidget"
                        @widget:copy="onCopySelectedWidget"
                        @widget:custom-setup="onOpenCustomForm"
                        @widget:reset="onResetCustomSettings"
                    />
                </div>
            </div>
        </div>
        <portal to="widget-modal-actions">
            <div
                v-if="customFormVisible"
                class="flex items-center gap-2 justify-end"
            >
                <RedesignBaseButton
                    type="soft"
                    class="min-w-30"
                    @click="onCloseCustomEditForm"
                >
                    {{ $t('general.back') }}
                </RedesignBaseButton>
                <RedesignBaseButton
                    class="min-w-30"
                    @click="onSaveCustomSettings"
                >
                    {{ $t('general.apply') }}
                </RedesignBaseButton>
            </div>
            <div
                v-else
                class="flex items-center gap-2 justify-end"
            >
                <RedesignBaseButton
                    type="soft"
                    class="min-w-30"
                    @click="onPrevStep"
                >
                    {{ $t('general.back') }}
                </RedesignBaseButton>
                <RedesignBaseButton
                    v-if="!isSummaryView"
                    type="soft"
                    class="min-w-30"
                    @click="onNextStep"
                >
                    {{ $t('general.next') }}
                </RedesignBaseButton>

                <RedesignBaseButton
                    v-else
                    :loading="loading"
                    :disabled="isOnSaveDisabled"
                    class="min-w-30"
                    @click="onFinish"
                >
                    {{ $t('common.submit') }}
                </RedesignBaseButton>
            </div>
        </portal>
    </div>
</template>

<script setup lang="ts">
import { BaseWidgetTemplate, IWidgetTemplateListItem } from '@/types/templates'
import { computed, nextTick, ref } from 'vue'
import cloneDeep from 'lodash/cloneDeep'
import { getDefaultGridLayout } from '@/helpers/util'
import { useUniqueIDWithPrefix } from '@/composables/useUniqueId'
import WidgetTemplateSelected from '@/components/RedesignComponents/Widgets/Create/WidgetTemplateSelected.vue'
import { WidgetConfig, WidgetTime } from '@/types/dashboard.d'
import uniqBy from 'lodash/uniqBy'
import merge from 'lodash/merge'
import { isCounterAgentsInStatus, isQueueDashboardWidget, shouldShowTimeFrameForWidgets } from '@/helpers/widgetUtils'
import {
    uniqueConfigKey,
    withTemplateDataTypes
} from '@/components/RedesignComponents/Widgets/widget.helper'
import orderBy from 'lodash/orderBy'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import { CATEGORY_PREVIEW, MAX_WIDGETS_LIMIT } from '@/components/RedesignComponents/Widgets/widgets.enum'
import WidgetTemplateCustomSetup from '@/components/RedesignComponents/Widgets/Create/WidgetTemplateCustomSetup.vue'
import WidgetTemplateSettingsForm from '@/components/RedesignComponents/Widgets/Create/WidgetTemplateSettingsForm.vue'
import isEqual from 'lodash/isEqual'
import useConfirmModal from '@/composables/useConfirmModal'
import i18n from '@/i18n'
import WidgetCustomisedMessage from '@/components/RedesignComponents/Widgets/Common/WidgetCustomisedMessage.vue'

const useUniqueID = useUniqueIDWithPrefix('new_widget')
const defaultWidgetTime = {
    Date_interval: '0',
    datedeff: '0',
    type: 'relative'
}

type TSelectedTemplateValue = { count: number, template: IWidgetTemplateListItem }
type TSelectedTemplate = Record<number, TSelectedTemplateValue>

const props = defineProps<{
    selectedTemplates: TSelectedTemplate
    totalWidgets?: number
    loading?: boolean
}>()
const emit = defineEmits([
    'update:step',
    'update:count',
    'open-custom-stem',
    'on-finish'
])

const customFormVisible = ref(false)
const widgetToSetup = ref<IWidgetTemplateListItem | undefined>(undefined)
const widgetToSetupTemplate = ref<string>('')
const finalStepVisible = ref(false)

const widgetList = ref<Array<IWidgetTemplateListItem>>([])
const uniqTemplatesConfigs = ref<Array<WidgetConfig>>([])

const wrapperRef = ref<HTMLElement>()
const widgetTemplateCustomSetupRef = ref<typeof WidgetTemplateCustomSetup>()
const widgetTemplateSettingsFormRef = ref<InstanceType<typeof WidgetTemplateSettingsForm>>()

const widgetUniqueTemplateModel = ref<Partial<IWidgetTemplateListItem>>({
    WidgetTime: {
        Date_interval: '0',
        datedeff: '',
        type: 'relative'
    },
    WidgetLayout: {
        ShowStatistics: [],
        SumOfOthers: false,
        AbsoluteNumbers: false,
        status: undefined
    }
})

/* Computed */
const widgetTimeModel = computed({
    get () {
        return widgetUniqueTemplateModel.value.WidgetTime || {
            Date_interval: '0',
            datedeff: '',
            type: 'relative'
        }
    },
    set (val) {
        widgetUniqueTemplateModel.value.WidgetTime = { ...val }
    }
})

const showStatisticsToDisplay = computed(() => {
    return widgetList.value.map(el => {
        return {
            ...el,
            DataTypeID: el.DataType?.DataTypeID
        }
    })
        .some(el => isQueueDashboardWidget(el))
})
const showStatusSelect = computed(() => {
    return widgetList.value.some(el => isCounterAgentsInStatus(el.DataType))
})
const showTimeFrame = computed(() => {
    return shouldShowTimeFrameForWidgets(widgetList.value)
})

const isOnSaveDisabled = computed(() => {
    return !Object.keys(props.selectedTemplates)?.length
})
const isAllCustomSettings = computed(() => {
    return widgetList.value.every(el => el.customSetup)
})
const isSummaryView = computed(() => {
    return isAllCustomSettings.value || finalStepVisible.value
})

/* Methods */
async function onNextStep () {
    const res = await  widgetTemplateSettingsFormRef.value?.validate()
    if (!res?.isValid) {
        return
    }
    finalStepVisible.value = true
}

function getTemplateGridHeight (template: IWidgetTemplateListItem): number {
    const defaultHeightPixels = template.TemplateSettings?.DefaultHeightPixels
    if (defaultHeightPixels) {
        return Math.floor(Number(defaultHeightPixels) / 80) * 2
    }
    return getDefaultGridLayout().height
}

function getTemplateGridWidth (template: IWidgetTemplateListItem): number {
    const defaultWidthPercentage = template.TemplateSettings?.DefaultWidthPrecentage
    if (defaultWidthPercentage) {
        return Math.min(Math.ceil(64 * Number(defaultWidthPercentage) / 100), 64)
    }
    return getDefaultGridLayout().width
}

function initWidgetList (setupData: Array<TSelectedTemplateValue>) {
    const _list: Array<IWidgetTemplateListItem> = []
    const baseLayout = getDefaultGridLayout()
    let currentY = baseLayout.y

    setupData.forEach(templateItem => {
        const _count = templateItem.count

        if (!_count) {
            return
        }

        let _index = -1

        while (++_index < _count) {
            const newWidget = cloneDeep(templateItem.template)

            const templateName = getTemplateNameTranslation(templateItem.template, templateItem.template.TemplateName)
            const widgetHeight = getTemplateGridHeight(templateItem.template)
            const widgetWidth = getTemplateGridWidth(templateItem.template)
            const gridLayout = {
                x: 0,
                y: currentY,
                width: widgetWidth,
                height: widgetHeight
            }
            currentY += widgetHeight

            _list.push({
                ...newWidget,
                uid: useUniqueID(),
                toStore: true,
                customSetup: false,
                WidgetLayout: { GridLayout: gridLayout },
                TemplateName: _index === 0
                    ? templateName
                    : `${templateName} ${_index}`
            })
        }
    })

    widgetList.value = [ ..._list ]
    initConfigs([ ..._list ])
}

function getTemplateNameTranslation (template: IWidgetTemplateListItem | BaseWidgetTemplate, fallback?: string): string {
    return i18n.te(template.TemplateNameContentTag)
        ? i18n.t(template.TemplateNameContentTag) as string
        : fallback ?? template.TemplateName
}

function initConfigs (list: Array<IWidgetTemplateListItem>) {
    widgetTimeModel.value = getInitialTimeModelData(list)
    uniqTemplatesConfigs.value = getUniqTemplatesConfigs(list)
}

function getUniqueConfigsMapped (template: IWidgetTemplateListItem) {

    const templateConfigs = template.DefaultWidgetConfig
        .map(config => withTemplateDataTypes(cloneDeep(config), template.TemplateSettings?.DataType))

    if (!uniqTemplatesConfigs.value.length) {
        return templateConfigs
    }

    return templateConfigs.map(config => {
        const existConfigs = uniqTemplatesConfigs.value
            .find(t => uniqueConfigKey(t) === uniqueConfigKey(config))

        return existConfigs ? cloneDeep(existConfigs) : config
    })
}

function getUniqTemplatesConfigs (templates: Array<IWidgetTemplateListItem>) {
    const perWidget = templates.filter(template => template.DefaultWidgetConfig)
        .map(template => ({
            title: template.TemplateName,
            configs: getUniqueConfigsMapped(template)
        }))

    const titlesByConfig = new Map<string, string[]>()
    perWidget.forEach(({ title, configs }) => {
        configs.forEach(config => {
            const key = uniqueConfigKey(config)
            const titles = titlesByConfig.get(key) ?? []

            if (title && !titles.includes(title)) {
                titles.push(title)
            }
            titlesByConfig.set(key, titles)
        })
    })

    const uniqTemplates = uniqBy(perWidget.flatMap(({ configs }) => configs), uniqueConfigKey)
        .map(config => ({
            ...config,
            widgetTitles: titlesByConfig.get(uniqueConfigKey(config)) ?? []
        }))
    return orderBy(uniqTemplates, [ 'ParameterType' ], [ 'desc' ]) as unknown as Array<WidgetConfig>
}

function getInitialTimeModelData (templates: Array<IWidgetTemplateListItem>) {
    const widgetTime = templates.reduce<Array<WidgetTime>>((acc, item) => {
        if (item.DefaultWidgetTime) {
            acc.push(item.DefaultWidgetTime)
        }
        return acc
    }, [])

    const actualWidgetTime = widgetTime.length ? uniqBy(widgetTime, 'DefaultWidgetTime')[0] : []

    return merge(defaultWidgetTime, actualWidgetTime)
}

function updateFilterItem (data: WidgetConfig) {
    uniqTemplatesConfigs.value = uniqTemplatesConfigs.value.map(i => {
        if (uniqueConfigKey(i) === uniqueConfigKey(data)) {
            return {
                ...data
            }
        }
        return i
    })
}

function onPrevStep () {
    if (finalStepVisible.value) {
        finalStepVisible.value = false
    } else {
        emit('update:step', CATEGORY_PREVIEW)
        nextTick(() => {
            resetAll()
        })
    }
}

function resetAll () {
    finalStepVisible.value = false
    customFormVisible.value = false
    widgetToSetup.value = undefined
    widgetToSetupTemplate.value = ''
    widgetList.value = []
    uniqTemplatesConfigs.value = []
    widgetUniqueTemplateModel.value = {
        WidgetTime: {
            Date_interval: '0',
            datedeff: '',
            type: 'relative'
        },
        WidgetLayout: {
            ShowStatistics: [],
            SumOfOthers: false,
            AbsoluteNumbers: false,
            status: undefined
        }
    }
}

function onFinish () {
    emit('on-finish', {
        widgetUniqueTemplateModel: widgetUniqueTemplateModel.value,
        widgetList: widgetList.value,
        uniqTemplatesConfigs: uniqTemplatesConfigs.value
    })
}

function onDeleteSelectedWidget (widget: IWidgetTemplateListItem) {
    widgetList.value = widgetList.value.filter(i => i.uid !== widget.uid)
    initConfigs([ ...widgetList.value ])
    const _templateId = widget.TemplateID
    const count = props.selectedTemplates[_templateId].count
    emit('update:count', widget.TemplateID, count - 1)
}

function onCopySelectedWidget (widget: IWidgetTemplateListItem) {
    if ((props.totalWidgets ?? 0) >= MAX_WIDGETS_LIMIT) {
        return
    }
    const newWidget = cloneDeep(widget)

    const selectedTemplate = props.selectedTemplates[widget.TemplateID]
    const widgetTemplate = selectedTemplate?.template

    const count = selectedTemplate?.count || 0
    const templateNameFallback = widgetTemplate?.TemplateName || widget.TemplateName

    let templateName = `${templateNameFallback} ${count + 1}`

    if (widgetTemplate) {
        templateName = `${getTemplateNameTranslation(widgetTemplate, templateName)} ${count + 1}`
    }

    widgetList.value.push({
        ...newWidget,
        uid: useUniqueID(),
        toStore: true,
        customSetup: widget.customSetup,
        WidgetLayout: {
            ...(widget.WidgetLayout || {}),
            GridLayout: getDefaultGridLayout()
        },
        TemplateName: templateName
    })
    initConfigs([ ...widgetList.value ])
    emit('update:count', widget.TemplateID, count + 1)
}

function onOpenCustomForm (widget: IWidgetTemplateListItem) {
    finalStepVisible.value = false
    const data = getWidgetToSetupData(widget)
    widgetToSetup.value = cloneDeep(data)
    widgetToSetupTemplate.value = getTemplateNameTranslation(widgetToSetup.value, widgetToSetup.value.TemplateName)
    customFormVisible.value = true
    scrollTop()
}

function getWidgetToSetupData (widget: IWidgetTemplateListItem) {
    const _data = {
        ...widget
    }
    if (!widget.customSetup) {
        _data.WidgetLayout = { ...widget.WidgetLayout }
        _data.WidgetTime = { ...widgetTimeModel.value }
        _data.WidgetConfig = [ ...widget.DefaultWidgetConfig ].map(config => {
            const i = withTemplateDataTypes({ ...config }, widget.TemplateSettings?.DataType)
            const uniqueData = uniqTemplatesConfigs.value
                .find(param => uniqueConfigKey(param) === uniqueConfigKey(i))

            if (uniqueData) {
                const merged = {
                    ...i,
                    ...uniqueData
                }
                delete merged.widgetTitles
                return merged
            }
            return i
        })
        if (isQueueDashboardWidget(widget)) {
            const { WidgetLayout } = widgetUniqueTemplateModel.value
            _data.WidgetLayout.ShowStatistics = WidgetLayout?.ShowStatistics
            _data.WidgetLayout.SumOfOthers = WidgetLayout?.SumOfOthers
            _data.WidgetLayout.AbsoluteNumbers = WidgetLayout?.AbsoluteNumbers
        }
        if (isCounterAgentsInStatus(widget.DataType)) {
            _data.WidgetLayout.status = widgetUniqueTemplateModel.value?.WidgetLayout?.status
        }
    }

    return _data
}

function isTheSameCustomConfigs () {
    if (!widgetToSetup.value) {
        return true
    }
    const widgetInList = widgetList.value.find(widget => widget.uid === widgetToSetup.value?.uid)
    return isEqual(widgetToSetup.value?.WidgetConfig, widgetInList?.WidgetConfig || widgetInList?.DefaultWidgetConfig)
}

function onCloseCustomEditForm () {
    const isChangedConfigs = !isTheSameCustomConfigs()
    if (isChangedConfigs) {
        useConfirmModal({
            type: 'confirm',
            message: String(i18n.t('widget.doYouWantToRemoveChangesAndGoToTheSettings'))
        }).then(res => {
            if (res) {
                closeCustomEditForm()
            }
        })
    } else {
        closeCustomEditForm()
    }
}

function closeCustomEditForm () {
    customFormVisible.value = false
    scrollTop()
    nextTick(() => {
        widgetToSetup.value = undefined
        widgetToSetupTemplate.value = ''
    })
}

function onSaveCustomSettings () {
    widgetTemplateCustomSetupRef.value?.validate()
        .then((res: { isValid: boolean }) => {
            if (res.isValid && widgetToSetup.value) {
                widgetList.value = widgetList.value.map(i => {
                    if (i.uid === widgetToSetup.value?.uid) {
                        return {
                            ...i,
                            ...widgetToSetup.value,
                            customSetup: true
                        }
                    }
                    return i
                })
                onCloseCustomEditForm()
            }
        })
}

function onResetCustomSettings (widgetItem: IWidgetTemplateListItem) {
    const res = getWidgetToSetupData({
        ...widgetItem,
        customSetup: false
    })
    widgetList.value = widgetList.value.map((item, index) => {
        if (item.uid === res?.uid) {
            const template = props.selectedTemplates[item.TemplateID]?.template

            let finalTemplateName = getTemplateNameTranslation(item, item.TemplateName)

            if (template) {
                finalTemplateName = `${getTemplateNameTranslation(template, template.TemplateName)} ${index + 1}`
            }

            return {
                ...item,
                ...res,
                TemplateName: finalTemplateName
            }
        }
        return item
    })

}

function initData () {
    const _values = Object.values(props.selectedTemplates || {})
    if (!_values.length) {
        widgetList.value = []
        return
    }
    initWidgetList(_values)
}

function scrollTop () {
    wrapperRef.value?.scrollIntoView(
        {
            behavior: 'smooth'
        }
    )
}

initData()
</script>
